import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { LogOut, Plus, Edit3, Trash2, Eye, UploadCloud, X, Maximize2 } from 'lucide-react';

interface Artwork {
  _id: string;
  title: string;
  description?: string;
  filename?: string;
  imageUrl?: string;    // ADD
  mimetype?: string;
  views?: number;
  createdAt?: string;
}
const AdminDashboard: React.FC = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [artworks, setArtworks] = useState<Artwork[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({ title: '', description: '' });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  
  // ✅ MODAL STATES (from GalleryGrid)
  const [selectedImage, setSelectedImage] = useState<Artwork | null>(null);
  const [imageScale, setImageScale] = useState(1);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const API_BASE = "https://art-gallery-backend-fabx.onrender.com";
  // Fetch artworks
  useEffect(() => {
    fetchArtworks();
  }, []);

  // Image preview
  useEffect(() => {
    if (imageFile) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(imageFile);
    } else {
      setImagePreview(null);
    }
  }, [imageFile]);

  const fetchArtworks = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/api/admin/artworks`);
      setArtworks(response.data.artworks || []);
    } catch (error) {
      console.error('Failed to fetch artworks:', error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ MODAL HANDLERS (from GalleryGrid)
  const openAdminModal = (artwork: Artwork) => {
    setSelectedImage(artwork);
    setImageScale(1);
    setIsLoadingImage(true);
    document.body.style.overflow = 'hidden';
  };

  const closeAdminModal = () => {
    setSelectedImage(null);
    setImageScale(1);
    document.body.style.overflow = 'unset';
  };

  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (!selectedImage) return;
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setImageScale(prev => Math.max(0.5, Math.min(3, prev * delta)));
  }, [selectedImage]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitLoading(true);

    try {
      console.log('📤 Frontend sending FormData');

      if (editingId) {
        // Update (text only)
        await axios.put(`${API_BASE}/api/admin/artworks/${editingId}`, formData, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
          }
        });
        console.log('✅ Updated artwork');
      } else {
        // Create with image
        const formDataToSend = new FormData();
        formDataToSend.append('title', formData.title);
        formDataToSend.append('description', formData.description || '');
        if (imageFile) {
          formDataToSend.append('image', imageFile);
        }

        await axios.post(`${API_BASE}/api/admin/artworks`, formDataToSend, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
          }
        });
        console.log('✅ Image uploaded successfully');
      }

      resetForm();
      await fetchArtworks();
    } catch (error: any) {
      console.error('❌ Upload failed:', error.response?.data || error.message);
    } finally {
      setSubmitLoading(false);
    }
  };

  const resetForm = () => {
    setShowUpload(false);
    setEditingId(null);
    setFormData({ title: '', description: '' });
    setImageFile(null);
    setImagePreview(null);
  };

  const handleEdit = (artwork: Artwork) => {
    setEditingId(artwork._id);
    setFormData({ title: artwork.title, description: artwork.description || '' });
    setShowUpload(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Delete this artwork permanently?')) {
      try {
        await axios.delete(`${API_BASE}/api/admin/artworks/${id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
          }
        });
        await fetchArtworks();
      } catch (error) {
        console.error('Delete failed');
      }
    }
  };

  return (
    <div className="min-h-screen bg-darkbg-900 text-white p-4 sm:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-12 gap-6">
          <h1 className="text-4xl lg:text-5xl font-black bg-gradient-to-r from-facebook-500 to-facebook-600 bg-clip-text text-transparent">
            Admin Dashboard
          </h1>
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={() => setShowUpload(!showUpload)}
              disabled={submitLoading}
              className="flex items-center gap-2 px-8 py-4 bg-facebook-600 hover:bg-facebook-700 disabled:bg-facebook-500 disabled:cursor-not-allowed text-white font-bold rounded-2xl shadow-2xl hover:shadow-facebook-500/25 transition-all"
            >
              <Plus className="w-5 h-5" />
              {showUpload ? 'Cancel' : 'Add New Artwork'}
            </button>
            <button
              onClick={() => {
                logout();
                navigate('/');
              }}
              className="flex items-center gap-2 px-8 py-4 bg-red-600/80 hover:bg-red-500/90 text-white font-bold rounded-2xl shadow-2xl hover:shadow-red-500/25 transition-all"
            >
              <LogOut className="w-5 h-5" />
              Logout
            </button>
          </div>
        </div>

        {/* Upload/Edit Form */}
        {showUpload && (
          <div className="bg-darkbg-800 p-8 lg:p-12 rounded-3xl border-2 border-facebook-500/30 mb-12 shadow-2xl">
            <h2 className="text-3xl font-bold mb-8 flex items-center gap-3 text-facebook-400">
              {editingId ? <Edit3 className="w-8 h-8" /> : <UploadCloud className="w-8 h-8" />}
              {editingId ? 'Edit Artwork' : 'Upload New Artwork'}
            </h2>
            
            <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Title */}
              <div>
                <label className="block text-sm font-semibold text-fgray-300 mb-3">Artwork Title *</label>
                <input
                  type="text"
                  placeholder="Enter artwork title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full p-5 bg-darkbg-700 border border-fgray-600 rounded-2xl text-white placeholder-fgray-500 focus:border-facebook-500 focus:outline-none focus:ring-2 focus:ring-facebook-500/30 transition-all"
                  required
                  disabled={submitLoading}
                />
              </div>

              {/* Description */}
              <div className="lg:col-span-2">
                <label className="block text-sm font-semibold text-fgray-300 mb-3">Description</label>
                <textarea
                  placeholder="Describe your artwork (optional)"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  className="w-full p-5 bg-darkbg-700 border border-fgray-600 rounded-2xl text-white placeholder-fgray-500 focus:border-facebook-500 focus:outline-none focus:ring-2 focus:ring-facebook-500/30 transition-all resize-none"
                  disabled={submitLoading}
                />
              </div>

              {/* Image Upload */}
              {!editingId && (
                <div className="lg:col-span-2">
                  <label className="block text-sm font-semibold text-fgray-300 mb-3 flex items-center gap-2">
                    <UploadCloud className="w-4 h-4" />
                    Artwork Image *
                  </label>
                  <input
                    id="image-upload"
                    type="file"
                    accept="image/*"
                    onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                    className="w-full p-4 bg-darkbg-700 border border-fgray-600 rounded-2xl text-white file:mr-4 file:py-3 file:px-6 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-facebook-600 file:text-white hover:file:bg-facebook-700"
                    disabled={submitLoading}
                    required
                  />
                  {imagePreview && (
                    <div className="mt-6 p-4 bg-darkbg-700 rounded-2xl border border-fgray-600">
                      <img src={imagePreview} alt="Preview" className="max-w-md max-h-64 object-cover rounded-xl mx-auto block shadow-2xl" />
                    </div>
                  )}
                </div>
              )}

              {/* Submit Button */}
              <div className="lg:col-span-2">
                <button
                  type="submit"
                  disabled={submitLoading || !formData.title || (!imageFile && !editingId)}
                  className="w-full py-5 px-10 bg-facebook-600 hover:bg-facebook-700 disabled:bg-facebook-500 disabled:cursor-not-allowed text-white font-bold text-xl rounded-3xl shadow-2xl hover:shadow-facebook-500/25 transition-all lg:py-6 lg:text-2xl"
                >
                  {submitLoading ? (
                    <>
                      <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3 inline-block" />
                      {editingId ? 'Updating...' : 'Uploading...'}
                    </>
                  ) : editingId ? (
                    'Update Artwork'
                  ) : (
                    'Upload Artwork'
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Artworks Table */}
        <div className="bg-darkbg-800 rounded-3xl border border-fgray-700 overflow-hidden shadow-2xl">
          {loading ? (
            <div className="p-16 text-center">Loading artworks...</div>
          ) : artworks.length === 0 ? (
            <div className="p-16 text-center text-fgray-400">
              <div className="text-6xl mb-8 opacity-20">🖼️</div>
              <h3 className="text-2xl font-bold mb-2">No artworks yet</h3>
              <p>Upload your first masterpiece!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-darkbg-700">
                    <th className="p-6 text-left font-bold text-facebook-400">Title</th>
                    <th className="p-6 text-left font-bold text-facebook-400 hidden lg:table-cell">Description</th>
                    <th className="p-6 text-right font-bold text-facebook-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {artworks.map((artwork) => (
                    <tr key={artwork._id} className="border-b border-fgray-700 hover:bg-darkbg-700">
                      <td className="p-6 font-semibold">{artwork.title}</td>
                      <td className="p-6 text-fgray-400 hidden lg:table-cell line-clamp-2">
                        {artwork.description || 'No description'}
                      </td>
                      <td className="p-6">
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => handleEdit(artwork)}
                            className="p-3 hover:bg-facebook-600/50 rounded-2xl text-facebook-400 hover:text-white transition-all"
                            title="Edit"
                          >
                            <Edit3 className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(artwork._id)}
                            className="p-3 hover:bg-red-600/50 rounded-2xl text-red-400 hover:text-red-100 transition-all"
                            title="Delete"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                          {/* ✅ EYE MODAL BUTTON */}
                          <button
                            onClick={() => openAdminModal(artwork)}
                            className="p-3 hover:bg-blue-500/50 rounded-2xl text-blue-400 hover:text-white transition-all"
                            title="View Image"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ✅ FULL MODAL (GalleryGrid EXACT COPY) */}
      {selectedImage && (
        <>
          <div 
            className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4 sm:p-8 animate-in fade-in zoom-in duration-300"
            onClick={closeAdminModal}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8">
            <div className="relative max-w-6xl max-h-[95vh] w-full h-full flex items-center justify-center animate-in fade-in zoom-in duration-300">
              <button
                onClick={closeAdminModal}
                className="absolute -top-4 right-4 w-12 h-12 bg-black/70 hover:bg-black/90 rounded-2xl flex items-center justify-center text-white transition-all z-10 backdrop-blur-sm shadow-2xl hover:scale-110"
              >
                <X className="w-6 h-6" />
              </button>
              
              <div className="absolute top-6 left-6 flex gap-3 bg-black/60 backdrop-blur-sm rounded-2xl p-3 z-10">
                <button
                  onClick={closeAdminModal}
                  className="w-12 h-12 rounded-xl bg-white/20 hover:bg-white/40 flex items-center justify-center text-white transition-all group hover:scale-110"
                  title="Exit fullscreen (ESC)"
                >
                  <Maximize2 className="w-6 h-6 group-hover:rotate-180 transition-transform duration-200" />
                </button>
              </div>

              {isLoadingImage && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-3xl">
                  <div className="w-16 h-16 border-4 border-white/30 border-t-white rounded-2xl animate-spin" />
                </div>
              )}

              <img
                src={`${API_BASE}/images/${selectedImage.filename}`}
                alt={selectedImage.title}
                className="max-w-full max-h-full object-contain rounded-3xl shadow-2xl cursor-grab active:cursor-grabbing select-none"
                style={{ transform: `scale(${imageScale})` }}
                onWheel={handleWheel}
                onLoad={() => setIsLoadingImage(false)}
                onError={() => setIsLoadingImage(false)}
              />

              <div className="absolute bottom-6 left-6 right-6 bg-black/80 backdrop-blur-xl rounded-2xl px-6 py-3 text-white max-w-4xl mx-auto border border-fgray-700/30 shadow-2xl">
                <div className="flex items-center justify-between text-sm">
                  <h2 className="font-bold text-white truncate flex-1 mr-4">
                    {selectedImage.title}
                  </h2>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-fgray-800/60 backdrop-blur-sm rounded-xl border border-fgray-600/40 text-fgray-200 font-semibold">
                    <Eye className="w-4 h-4 text-facebook-400" />
                    <span>{selectedImage.views?.toLocaleString() || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
